import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock, X, AlertCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { fmt } from "../lib/format";

/**
 * Page hit by Mercado Pago after Checkout Pro redirect.
 * Receives query params: status, payment_id, external_reference, order_id, etc.
 * Polls backend until the webhook has updated the order, or times out.
 */
export default function CheckoutResult() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null); // backend order status
  const [orderInfo, setOrderInfo] = useState(null);
  const [polling, setPolling] = useState(true);
  const [attempts, setAttempts] = useState(0);

  const mpStatus = searchParams.get("status"); // approved | pending | failure
  const externalRef = searchParams.get("external_reference");
  const queryOrderId = searchParams.get("order_id");
  const orderId =
    queryOrderId ||
    externalRef ||
    (typeof window !== "undefined" ? localStorage.getItem("biofarmax:last_order_id") : null);

  const fetchStatus = useCallback(async () => {
    if (!orderId) {
      setPolling(false);
      return;
    }
    try {
      const { data } = await api.get(`/orders/${orderId}/payment-status`);
      setOrderInfo(data);
      setStatus(data.status);
      if (["paid", "cancelled", "refunded"].includes(data.status)) {
        setPolling(false);
      }
    } catch {
      // keep polling
    }
  }, [orderId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!polling) return;
    if (attempts >= 12) {
      setPolling(false);
      return;
    }
    const t = setTimeout(() => {
      setAttempts((a) => a + 1);
      fetchStatus();
    }, 2500);
    return () => clearTimeout(t);
  }, [attempts, polling, fetchStatus]);

  const visual = (() => {
    if (status === "paid") {
      return {
        Icon: Check,
        color: "text-status-success",
        bg: "bg-status-success/15",
        title: "Pagamento aprovado!",
        msg: "Recebemos seu pagamento. Vamos preparar seu pedido.",
      };
    }
    if (status === "cancelled") {
      return {
        Icon: X,
        color: "text-status-error",
        bg: "bg-status-error/15",
        title: "Pagamento não aprovado",
        msg: "Não foi possível concluir o pagamento. Tente novamente ou use outro método.",
      };
    }
    if (mpStatus === "failure") {
      return {
        Icon: X,
        color: "text-status-error",
        bg: "bg-status-error/15",
        title: "Pagamento recusado",
        msg: "Sua compra não foi finalizada. Você pode tentar de novo no carrinho.",
      };
    }
    if (mpStatus === "pending" || status === "pending_payment") {
      return {
        Icon: Clock,
        color: "text-brand-accent",
        bg: "bg-brand-accent/15",
        title: "Pagamento em análise",
        msg:
          "Estamos aguardando a confirmação do Mercado Pago. Você receberá um e-mail assim que for aprovado.",
      };
    }
    return {
      Icon: AlertCircle,
      color: "text-ink-secondary",
      bg: "bg-bg-secondary",
      title: "Verificando status…",
      msg: "Estamos consultando o Mercado Pago. Isso pode levar alguns segundos.",
    };
  })();

  const { Icon } = visual;

  return (
    <div className="pt-32 pb-24 max-w-2xl mx-auto px-5" data-testid="checkout-result">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
      >
        <div
          className={`w-20 h-20 mx-auto mb-6 rounded-full ${visual.bg} grid place-items-center`}
        >
          {polling && !status ? (
            <Loader2 size={42} className="text-ink-secondary animate-spin" />
          ) : (
            <Icon size={42} className={visual.color} strokeWidth={2.5} />
          )}
        </div>
        <div className="eyebrow mb-3">Mercado Pago</div>
        <h1 className="h-display text-4xl sm:text-5xl mb-4" data-testid="checkout-result-title">
          {visual.title}
        </h1>
        <p className="text-ink-secondary mb-8 max-w-md mx-auto">{visual.msg}</p>

        {orderInfo && (
          <div className="bg-bg-secondary rounded-3xl p-6 mb-8 text-left">
            <div className="text-xs uppercase tracking-widest font-tech text-ink-secondary mb-2">
              Pedido
            </div>
            <div className="font-tech text-lg" data-testid="result-order-id">
              {orderInfo.order_id}
            </div>
            {orderInfo.total != null && (
              <div className="text-sm mt-2 text-ink-secondary">
                Total: <strong>{fmt(orderInfo.total)}</strong>
              </div>
            )}
            {orderInfo.mp_status && (
              <div className="text-xs text-ink-secondary mt-2 font-tech">
                MP: {orderInfo.mp_status}
                {orderInfo.mp_status_detail ? ` · ${orderInfo.mp_status_detail}` : ""}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/conta" className="btn-primary" data-testid="result-view-orders-btn">
            Meus pedidos
          </Link>
          <Link to="/loja" className="btn-outline" data-testid="result-back-store-btn">
            Continuar comprando
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
